/**
 * Recharge Subscription Widget Price Updater
 * 
 * Monitors the Recharge subscription widget and dynamically updates
 * the product price display when switching between one-time and subscription options.
 * 
 * Features:
 * - Detects Recharge widget loading (async)
 * - Monitors radio button changes for one-time vs subscription
 * - Updates price with smooth fade transition
 * - Handles widget updates and re-renders
 * - Compatible with Shopify Online Store 2.0
 * - Supports variant changes
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    // Selectors - using safe, flexible selectors
    WIDGET_CONTAINER: '.rc-widget',
    RADIO_INPUT: 'input[type="radio"], .rc-option__input',
    OPTION_CONTAINER: '.rc-option, .rc-radio',
    PRICE_ELEMENT: '.price-item--regular',
    PRICE_CONTAINER: '.price__regular, .price__sale',
    // Animation settings
    TRANSITION_DURATION: 300, // milliseconds
    FADE_OPACITY: 0.3
  };

  /**
   * Recharge Price Updater Class
   */
  class RechargePriceUpdater {
    constructor() {
      this.widgetContainer = null;
      this.priceElement = null;
      this.oneTimePrice = null;
      this.subscriptionPrice = null;
      this.isSubscription = false;
      this.observer = null;
      this.radioObserver = null;
      this.variantObserver = null;
      this.isInitialized = false;
      this.currentVariantId = null;
      this.discountedOneTimePrice = null;
      this.isFetchingDiscount = false;
      this.discountPercentage = null;
      
      // Bind methods
      this.handleRadioChange = this.handleRadioChange.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.init = this.init.bind(this);
    }

    /**
     * Initialize the price updater
     */
    init() {
      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.init);
        return;
      }

      // Only run on product pages
      if (!document.querySelector('[data-product-id]')) {
        return;
      }

      // Find price element
      this.findPriceElement();
      
      if (!this.priceElement) {
        console.warn('Recharge Price Updater: Price element not found');
        return;
      }

      // Store initial one-time price
      this.captureCurrentPrices();

      // Start watching for widget
      this.watchForWidget();
      
      // Watch for variant changes
      this.watchForVariantChanges();
    }

    /**
     * Capture current prices from the page
     */
    captureCurrentPrices() {
      // Try to get one-time price from regular price div first
      const regularDiv = document.querySelector('.price__regular');
      let oneTimePriceElement = null;
      
      if (regularDiv) {
        oneTimePriceElement = regularDiv.querySelector('.price-item--regular');
      }
      
      // If not found, try sale price div (original price might be there)
      if (!oneTimePriceElement) {
        const saleDiv = document.querySelector('.price__sale');
        if (saleDiv) {
          // Get the strikethrough price (original price)
          const strikethroughPrice = saleDiv.querySelector('s.price-item--regular');
          if (strikethroughPrice) {
            oneTimePriceElement = strikethroughPrice;
          } else {
            // If no strikethrough, the sale price div might have the regular price
            oneTimePriceElement = saleDiv.querySelector('.price-item--regular');
          }
        }
      }
      
      // Fallback to current price element
      if (!oneTimePriceElement) {
        oneTimePriceElement = this.priceElement;
      }
      
      // Store initial one-time price
      this.oneTimePrice = this.extractPriceFromElement(oneTimePriceElement);
      
      // Ensure one-time price has currency code if original doesn't have it
      if (this.oneTimePrice && !/\b(USD|CAD|EUR|GBP|AUD|JPY|CNY|INR|MXN|BRL|ZAR|KRW|SGD|HKD|NZD|CHF|SEK|NOK|DKK|PLN|RUB|TRY|CZK|HUF|ILS|CLP|PHP|THB|MYR|IDR|VND|ARS)\b/i.test(this.oneTimePrice)) {
        // Add USD if currency code is missing
        this.oneTimePrice = `${this.oneTimePrice} USD`;
      }
      
      // Get current variant ID if available
      this.currentVariantId = this.getCurrentVariantId();
      
      // Reset subscription price so it gets recalculated
      this.subscriptionPrice = null;
    }

    /**
     * Get current variant ID from the page
     */
    getCurrentVariantId() {
      // Try multiple methods to get variant ID
      
      // Method 1: From variant select/radio
      const variantInput = document.querySelector('[name="id"], [name="variant"]');
      if (variantInput && variantInput.value) {
        return variantInput.value;
      }
      
      // Method 2: From hidden input
      const hiddenVariant = document.querySelector('input[type="hidden"][name="id"]');
      if (hiddenVariant && hiddenVariant.value) {
        return hiddenVariant.value;
      }
      
      // Method 3: From data attribute
      const productForm = document.querySelector('form[action*="/cart/add"]');
      if (productForm) {
        const variantId = productForm.querySelector('[data-variant-id]');
        if (variantId) {
          return variantId.getAttribute('data-variant-id');
        }
      }
      
      return null;
    }

    /**
     * Watch for variant changes
     */
    watchForVariantChanges() {
      // Method 1: Listen for variant select changes
      const variantSelectors = document.querySelectorAll('select[name="id"], input[name="id"], [data-variant-picker]');
      variantSelectors.forEach(selector => {
        selector.addEventListener('change', this.handleVariantChange);
      });
      
      // Method 2: Listen for option changes (swatches, buttons)
      const optionInputs = document.querySelectorAll('input[name^="options"], [name^="option"]');
      optionInputs.forEach(input => {
        input.addEventListener('change', this.handleVariantChange);
      });
      
      // Method 3: Watch for price element changes (most reliable)
      if (this.priceElement) {
        this.variantObserver = new MutationObserver((mutations) => {
          // Check if subscription is selected - if so, don't let product-info.js update interfere
          const isSubscriptionSelected = this.isSubscriptionCurrentlySelected();
          
          if (isSubscriptionSelected) {
            // Subscription is selected - get subscription price directly
            setTimeout(() => {
              if (this.widgetContainer) {
                this.captureCurrentPrices();
                this.subscriptionPrice = this.getSubscriptionPriceFromWidget();
                if (this.subscriptionPrice) {
                  this.updatePrice(this.subscriptionPrice, true);
                }
              }
            }, 100);
          } else {
            // Check if price text changed (indicates variant change)
            const newPrice = this.extractPriceFromElement(this.priceElement);
            if (newPrice && newPrice !== this.oneTimePrice) {
              this.handleVariantChange();
            }
          }
        });
        
        this.variantObserver.observe(this.priceElement, {
          childList: true,
          characterData: true,
          subtree: true
        });
      }
      
      // Method 4: Watch for variant ID changes in form
      const productForm = document.querySelector('form[action*="/cart/add"]');
      if (productForm) {
        const formObserver = new MutationObserver(() => {
          const newVariantId = this.getCurrentVariantId();
          if (newVariantId && newVariantId !== this.currentVariantId) {
            this.handleVariantChange();
          }
        });
        
        formObserver.observe(productForm, {
          attributes: true,
          childList: true,
          subtree: true,
          attributeFilter: ['value', 'data-variant-id']
        });
      }
      
      // Method 5: Listen for Shopify variant change events
      document.addEventListener('variant:change', this.handleVariantChange);
      document.addEventListener('variantChange', this.handleVariantChange);
      
      // Method 6: Listen for quantity variant picker changes
      const quantityVariantSelect = document.querySelector('[data-quantity-variant-select]');
      if (quantityVariantSelect) {
        quantityVariantSelect.addEventListener('change', () => {
          setTimeout(() => {
            this.handleVariantChange();
          }, 150);
        });
      }
      
      // Method 7: Watch for variant-selects changes (quantity variant picker)
      const variantSelects = document.querySelector('variant-selects');
      if (variantSelects) {
        const variantSelectsObserver = new MutationObserver(() => {
          setTimeout(() => {
            this.handleVariantChange();
          }, 150);
        });
        
        variantSelectsObserver.observe(variantSelects, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['data-selected-variant']
        });
      }
    }

    /**
     * Check if subscription is currently selected (without event)
     */
    isSubscriptionCurrentlySelected() {
      if (!this.widgetContainer) return false;
      
      const subscriptionRadio = this.widgetContainer.querySelector('input[name="purchaseOption"][value="subscription"]');
      if (subscriptionRadio && subscriptionRadio.checked) {
        return true;
      }
      
      // Also check by active class
      const subscriptionLabel = this.widgetContainer.querySelector('.rc-radio.subscription-radio.rc-radio--active');
      if (subscriptionLabel) {
        return true;
      }
      
      return false;
    }

    /**
     * Handle variant change
     */
    handleVariantChange() {
      // Check subscription state immediately to prevent flicker
      const isSubscriptionSelected = this.isSubscriptionCurrentlySelected();
      
      // Show loading state immediately to prevent showing wrong price
      
      
      // Small delay to ensure price has updated
      setTimeout(async () => {
        const newVariantId = this.getCurrentVariantId();
        
        // Only reinitialize if variant actually changed
        if (newVariantId !== this.currentVariantId) {
          this.currentVariantId = newVariantId;
          
          // Re-find price element (might have changed)
          this.findPriceElement();
          
          if (!this.priceElement) {
            
            return;
          }
          
          // Capture new prices
          this.captureCurrentPrices();
          
          // Reset discounted price
          this.discountedOneTimePrice = null;
          this.discountPercentage = null;
          
          // If subscription is selected, prioritize subscription price
          if (isSubscriptionSelected && this.widgetContainer) {
            // Get subscription price from widget
            this.subscriptionPrice = this.getSubscriptionPriceFromWidget();
            
            if (this.subscriptionPrice) {
              // Directly update to subscription price - no flicker
              this.updatePrice(this.subscriptionPrice, true);
              return;
            } else {
              // Wait a bit for widget to update, then try again
              setTimeout(() => {
                this.subscriptionPrice = this.getSubscriptionPriceFromWidget();
                if (this.subscriptionPrice) {
                  this.updatePrice(this.subscriptionPrice, true);
                } else {
                  
                }
              }, 200);
              return;
            }
          }
          
          // If one-time is selected, check for discounts
          if (!isSubscriptionSelected) {
            await this.checkAndUpdateOneTimePrice();
          } else {
            
          }
        } else {
          
        }
      }, 50);
    }

    /**
     * Show loading state on price to prevent flicker
     */
    showPriceLoading() {
      if (!this.priceElement) return;
      
      const priceContainer = this.priceElement.closest('.price__container') || 
                           this.priceElement.closest('.price') ||
                           this.priceElement.parentElement;
      
      if (priceContainer) {
        priceContainer.style.opacity = '0.5';
        priceContainer.style.transition = 'opacity 0.2s';
        
        // Add loading class if it doesn't exist
        if (!priceContainer.querySelector('.price-loading')) {
          const loader = document.createElement('span');
          loader.className = 'price-loading';
          loader.style.cssText = 'display: inline-block; width: 16px; height: 16px; border: 2px solid #f3f3f3; border-top: 2px solid #151F6D; border-radius: 50%; animation: spin 0.6s linear infinite; margin-left: 8px;';
          priceContainer.appendChild(loader);
          
          // Add spin animation if not exists
          if (!document.querySelector('#price-loader-style')) {
            const style = document.createElement('style');
            style.id = 'price-loader-style';
            style.textContent = '@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }';
            document.head.appendChild(style);
          }
        }
      }
    }

    /**
     * Hide loading state on price
     */
    hidePriceLoading() {
      if (!this.priceElement) return;
      
      const priceContainer = this.priceElement.closest('.price__container') || 
                           this.priceElement.closest('.price') ||
                           this.priceElement.parentElement;
      
      if (priceContainer) {
        priceContainer.style.opacity = '1';
        
        const loader = priceContainer.querySelector('.price-loading');
        if (loader) {
          loader.remove();
        }
      }
    }

    /**
     * Find the price element to update
     */
    findPriceElement() {
      // Try multiple selectors to find the price
      // First, try to find price-item--regular in price__regular
      const regularDiv = document.querySelector('.price__regular .price-item--regular');
      if (regularDiv) {
        this.priceElement = regularDiv;
        return;
      }

      // Try price-item--regular anywhere
      this.priceElement = document.querySelector(CONFIG.PRICE_ELEMENT);
      
      if (!this.priceElement) {
        // Fallback: look for price container and find regular price inside
        const priceContainer = document.querySelector(CONFIG.PRICE_CONTAINER);
        if (priceContainer) {
          this.priceElement = priceContainer.querySelector(CONFIG.PRICE_ELEMENT);
        }
      }

      // Last resort: find any price-item
      if (!this.priceElement) {
        this.priceElement = document.querySelector('.price-item');
      }
    }

    /**
     * Extract numeric price from element text
     * @param {HTMLElement} element - The price element
     * @returns {string} - The price text (e.g., "$29.99")
     */
    extractPriceFromElement(element) {
      if (!element) return null;
      
      // Get text content, removing any strikethrough or hidden elements
      let priceText = element.textContent.trim();
      
      // First, extract the price pattern (currency symbol + digits + decimal)
      const priceMatch = priceText.match(/[$€£¥]?\s*[\d,]+\.?\d*/);
      if (priceMatch) {
        priceText = priceMatch[0];
        
        // Remove discount badges and promotional text
        const cleanedText = element.textContent
          .replace(/(SUBSCRIBE\s*)?(SAVE\s*)?\d+%/gi, '')
          .replace(/SUBSCRIBE/gi, '')
          .replace(/SAVE\s*\d+%/gi, '')
          .replace(/\d+%\s*(OFF|DISCOUNT)?/gi, '')
          .replace(/\b(SAVE|SUBSCRIBE|DISCOUNT|OFF)\b/gi, '')
          .trim();
        
        const cleanedPriceMatch = cleanedText.match(/[$€£¥]?\s*[\d,]+\.?\d*/);
        if (cleanedPriceMatch) {
          priceText = cleanedPriceMatch[0];
        }
      } else {
        priceText = priceText
          .replace(/(SUBSCRIBE\s*)?(SAVE\s*)?\d+%/gi, '')
          .replace(/SUBSCRIBE/gi, '')
          .replace(/SAVE\s*\d+%/gi, '')
          .replace(/\d+%\s*(OFF|DISCOUNT)?/gi, '')
          .replace(/\b(SAVE|SUBSCRIBE|DISCOUNT|OFF)\b/gi, '')
          .trim();
      }
      
      priceText = priceText.replace(/\s+/g, ' ').trim();
      
      return priceText;
    }

    /**
     * Calculate subscription price from one-time price
     */
    calculateSubscriptionPrice(oneTimePrice, discountPercent = null) {
      if (!discountPercent) {
        const searchContainer = this.widgetContainer || document;
        const subscriptionBadge = searchContainer.querySelector('.subscription-badge');
        if (subscriptionBadge) {
          const badgeText = subscriptionBadge.textContent;
          const match = badgeText.match(/(\d+)%/);
          if (match) {
            discountPercent = parseFloat(match[1]);
          }
        }
      }

      if (!discountPercent) {
        const searchContainer = this.widgetContainer || document;
        const discountElement = searchContainer.querySelector('.rc-widget .rc-discount, .rc-widget [class*="discount"]');
        if (discountElement) {
          const discountText = discountElement.textContent;
          const match = discountText.match(/(\d+)%/);
          if (match) {
            discountPercent = parseFloat(match[1]);
          }
        }
      }

      if (!discountPercent) {
        const searchContainer = this.widgetContainer || document;
        const subscriptionOption = searchContainer.querySelector('.rc-widget [class*="subscription"]');
        if (subscriptionOption) {
          const labelText = subscriptionOption.textContent;
          const match = labelText.match(/(\d+)%/);
          if (match) {
            discountPercent = parseFloat(match[1]);
          }
        }
      }

      const priceMatch = oneTimePrice.match(/[\d,]+\.?\d*/);
      if (!priceMatch) return oneTimePrice;

      const numericPrice = parseFloat(priceMatch[0].replace(/,/g, ''));
      if (isNaN(numericPrice)) return oneTimePrice;

      if (discountPercent) {
        const discountedPrice = numericPrice * (1 - discountPercent / 100);
        const formattedPrice = this.formatPrice(discountedPrice, oneTimePrice);
        return formattedPrice;
      }

      return oneTimePrice;
    }

    /**
     * Format price to match original format
     */
    formatPrice(price, originalFormat) {
      const hasCurrency = /[$€£¥]/.test(originalFormat);
      const currencySymbol = originalFormat.match(/[$€£¥]/)?.[0] || '$';
      
      const hasCurrencyCode = /\b(USD|CAD|EUR|GBP|AUD|JPY|CNY|INR|MXN|BRL|ZAR|KRW|SGD|HKD|NZD|CHF|SEK|NOK|DKK|PLN|RUB|TRY|CZK|HUF|ILS|CLP|PHP|THB|MYR|IDR|VND|ARS)\b/i.test(originalFormat);
      const currencyCode = originalFormat.match(/\b(USD|CAD|EUR|GBP|AUD|JPY|CNY|INR|MXN|BRL|ZAR|KRW|SGD|HKD|NZD|CHF|SEK|NOK|DKK|PLN|RUB|TRY|CZK|HUF|ILS|CLP|PHP|THB|MYR|IDR|VND|ARS)\b/i)?.[0] || 'USD';
      
      const formatted = price.toFixed(2);
      
      if (hasCurrency) {
        return hasCurrencyCode ? `${currencySymbol}${formatted} ${currencyCode}` : `${currencySymbol}${formatted} USD`;
      }
      
      return hasCurrencyCode ? `${formatted} ${currencyCode}` : `${formatted} USD`;
    }

    /**
     * Get subscription price from Recharge widget
     */
    getSubscriptionPriceFromWidget() {
      if (!this.widgetContainer) return null;

      const subscriptionRadio = this.widgetContainer.querySelector(
        '.rc-radio.subscription-radio, .rc-radio[value="subscription"], .rc-option[class*="subscription"]'
      );
      
      if (subscriptionRadio) {
        const priceLabel = subscriptionRadio.querySelector('.price-label');
        if (priceLabel) {
          let price = this.extractPriceFromElement(priceLabel);
          if (price && price !== this.oneTimePrice) {
            if (!/\b(USD|CAD|EUR|GBP|AUD|JPY|CNY|INR|MXN|BRL|ZAR|KRW|SGD|HKD|NZD|CHF|SEK|NOK|DKK|PLN|RUB|TRY|CZK|HUF|ILS|CLP|PHP|THB|MYR|IDR|VND|ARS)\b/i.test(price)) {
              price = `${price} USD`;
            }
            return price;
          }
        }
      }

      const selectors = [
        '.rc-widget [class*="subscription-price"]',
        '.rc-widget [class*="price"] [class*="subscription"]',
        '.rc-widget .rc-option--subscription [class*="price"]',
        '.rc-widget .rc-option[value*="subscription"] [class*="price"]',
        '.rc-widget [data-subscription-price]',
        '.rc-widget [data-price][data-subscription]',
        '.rc-widget [class*="selling-plan"] [class*="price"]'
      ];

      for (const selector of selectors) {
        const element = this.widgetContainer.querySelector(selector);
        if (element) {
          let price = this.extractPriceFromElement(element);
          if (price && price !== this.oneTimePrice) {
            if (!/\b(USD|CAD|EUR|GBP|AUD|JPY|CNY|INR|MXN|BRL|ZAR|KRW|SGD|HKD|NZD|CHF|SEK|NOK|DKK|PLN|RUB|TRY|CZK|HUF|ILS|CLP|PHP|THB|MYR|IDR|VND|ARS)\b/i.test(price)) {
              price = `${price} USD`;
            }
            return price;
          }
        }
      }

      return this.calculateSubscriptionPrice(this.oneTimePrice);
    }

    /**
     * Watch for Recharge widget to appear in DOM
     */
    watchForWidget() {
      this.widgetContainer = document.querySelector(CONFIG.WIDGET_CONTAINER);
      
      if (this.widgetContainer) {
        this.initializeWidget();
      } else {
        this.observer = new MutationObserver((mutations) => {
          const widget = document.querySelector(CONFIG.WIDGET_CONTAINER);
          if (widget && !this.isInitialized) {
            this.widgetContainer = widget;
            this.initializeWidget();
          }
        });

        this.observer.observe(document.body, {
          childList: true,
          subtree: true
        });
      }
    }

    /**
     * Initialize widget monitoring once it's found
     */
    async initializeWidget() {
      if (this.isInitialized) return;

      this.subscriptionPrice = this.getSubscriptionPriceFromWidget();
      this.attachRadioListeners();
      this.watchWidgetUpdates();

      this.isInitialized = true;

      if (this.observer) {
        this.observer.disconnect();
      }

      // Check initial selection and update price accordingly
      const oneTimeRadio = this.widgetContainer?.querySelector('input[name="purchaseOption"][value="onetime"]');
      const subscriptionRadio = this.widgetContainer?.querySelector('input[name="purchaseOption"][value="subscription"]');
      
      if (subscriptionRadio && subscriptionRadio.checked) {
        // Subscription is selected
        if (this.subscriptionPrice) {
          this.updatePrice(this.subscriptionPrice, true);
        }
      } else {
        // One-time is selected (or default), check for discounts
        await this.checkAndUpdateOneTimePrice();
        // Badge will be shown automatically if discount applies in checkAndUpdateOneTimePrice
      }
    }

    /**
     * Attach event listeners to radio buttons
     */
    attachRadioListeners() {
      if (!this.widgetContainer) return;

      const radioInputs = this.widgetContainer.querySelectorAll(CONFIG.RADIO_INPUT);
      
      if (radioInputs.length === 0) {
        this.watchForRadioButtons();
        return;
      }

      radioInputs.forEach(radio => {
        radio.addEventListener('change', this.handleRadioChange);
        
        if (radio.checked) {
          this.checkInitialSelection(radio);
        }
      });
    }

    /**
     * Watch for radio buttons to appear
     */
    watchForRadioButtons() {
      if (this.radioObserver) return;

      this.radioObserver = new MutationObserver(() => {
        const radioInputs = this.widgetContainer?.querySelectorAll(CONFIG.RADIO_INPUT);
        if (radioInputs && radioInputs.length > 0) {
          this.attachRadioListeners();
          if (this.radioObserver) {
            this.radioObserver.disconnect();
            this.radioObserver = null;
          }
        }
      });

      if (this.widgetContainer) {
        this.radioObserver.observe(this.widgetContainer, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['checked']
        });
      }
    }

    /**
     * Check initial radio selection state
     */
    checkInitialSelection(radio) {
      const isSubscription = this.isSubscriptionSelected(radio);
      
      if (isSubscription && this.subscriptionPrice) {
        this.updatePrice(this.subscriptionPrice, true);
      }
    }

    /**
     * Determine if subscription option is selected
     */
    isSubscriptionSelected(radio) {
      const optionContainer = radio.closest(CONFIG.OPTION_CONTAINER);
      if (!optionContainer) return false;

      const containerClasses = optionContainer.className.toLowerCase();
      const hasSubscription = 
        containerClasses.includes('subscription') ||
        containerClasses.includes('subscribe') ||
        radio.value?.toLowerCase().includes('subscription') ||
        radio.id?.toLowerCase().includes('subscription');

      const isOneTime = 
        containerClasses.includes('onetime') ||
        containerClasses.includes('one-time') ||
        radio.value?.toLowerCase().includes('onetime') ||
        radio.id?.toLowerCase().includes('onetime');

      return hasSubscription && !isOneTime;
    }

    /**
     * Handle radio button change event
     */
    async handleRadioChange(event) {
      const radio = event.target;
      
      if (!radio.checked) return;

      const isSubscription = this.isSubscriptionSelected(radio);
      
      if (isSubscription) {
        if (this.subscriptionPrice) {
          this.updatePrice(this.subscriptionPrice, true);
        } else {
          const newSubscriptionPrice = this.getSubscriptionPriceFromWidget();
          if (newSubscriptionPrice) {
            this.subscriptionPrice = newSubscriptionPrice;
            this.updatePrice(newSubscriptionPrice, true);
          }
        }
      } else {
        // For one-time purchase, check if automatic discount applies
        await this.checkAndUpdateOneTimePrice();
        // Hide badge if switching from subscription to one-time (will show if discount applies)
        if (!this.discountPercentage) {
          this.hideDiscountBadge();
        }
      }

      this.isSubscription = isSubscription;
    }

    /**
     * Check if automatic discount applies for one-time purchase and update price
     */
    async checkAndUpdateOneTimePrice() {
      if (this.isFetchingDiscount) return;
      
      this.isFetchingDiscount = true;
      
      try {
        const variantId = this.getCurrentVariantId();
        if (!variantId) {
          this.updatePrice(this.oneTimePrice, false);
          this.isFetchingDiscount = false;
          return;
        }

        // Fetch discounted price from cart API
        const discountedPrice = await this.getDiscountedPriceFromCart(variantId);
        
        if (discountedPrice) {
          // Extract numeric values for comparison
          const discountedNumeric = this.extractNumericPrice(discountedPrice);
          const oneTimeNumeric = this.extractNumericPrice(this.oneTimePrice);
          
          if (discountedNumeric && oneTimeNumeric && discountedNumeric < oneTimeNumeric) {
            // Discount applies - calculate discount percentage
            const discountPercent = Math.round(((oneTimeNumeric - discountedNumeric) / oneTimeNumeric) * 100);
            this.discountPercentage = discountPercent;
            this.discountedOneTimePrice = discountedPrice;
            // Pass false for isSubscription since this is a one-time discount
            this.updatePrice(discountedPrice, false, this.oneTimePrice);
          } else {
            // No discount - show regular price
            this.discountPercentage = null;
            this.discountedOneTimePrice = null;
            this.updatePrice(this.oneTimePrice, false);
          }
        } else {
          // No discount - show regular price
          this.discountPercentage = null;
          this.discountedOneTimePrice = null;
          this.updatePrice(this.oneTimePrice, false);
        }
      } catch (error) {
        console.error('Error checking discount:', error);
        // On error, show regular price
        this.discountPercentage = null;
        this.updatePrice(this.oneTimePrice, false);
      } finally {
        this.isFetchingDiscount = false;
      }
    }

    /**
     * Extract numeric price value from price string for comparison
     */
    extractNumericPrice(priceString) {
      if (!priceString) return null;
      const match = priceString.match(/[\d,]+\.?\d*/);
      if (match) {
        return parseFloat(match[0].replace(/,/g, ''));
      }
      return null;
    }

    /**
     * Get discounted price from cart API for automatic discounts
     */
    async getDiscountedPriceFromCart(variantId) {
      try {
        // Get current cart
        const currentCartResponse = await fetch('/cart.js');
        if (!currentCartResponse.ok) return null;
        
        const currentCart = await currentCartResponse.json();
        
        // Check if variant is already in cart
        const existingItem = currentCart.items.find(item => item.variant_id === parseInt(variantId));
        let itemToCheck = existingItem;
        let shouldRemoveItem = false;
        
        if (!existingItem) {
          // Add variant to cart temporarily to get discounted price
          const addResponse = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: variantId,
              quantity: 1
            })
          });

          if (!addResponse.ok) {
            return null;
          }

          // Get updated cart with discounted price
          const updatedCartResponse = await fetch('/cart.js');
          if (!updatedCartResponse.ok) return null;
          
          const updatedCart = await updatedCartResponse.json();
          itemToCheck = updatedCart.items.find(item => item.variant_id === parseInt(variantId));
          shouldRemoveItem = true;
        }
        
        if (itemToCheck) {
          // Get prices - use final_price for discounted price, original_price for original
          const discountedPrice = itemToCheck.final_price || itemToCheck.price;
          const originalLinePrice = itemToCheck.original_price || itemToCheck.price;
          
          // Check if there's a discount (automatic discount applied)
          if (discountedPrice < originalLinePrice) {
            // Format the price to match existing format
            const formattedPrice = this.formatPriceFromCents(discountedPrice);
            
            // Remove the item from cart if we added it temporarily
            if (shouldRemoveItem && itemToCheck.key) {
              await fetch('/cart/change.js', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  id: itemToCheck.key,
                  quantity: 0
                })
              }).catch(err => {
                console.warn('Could not remove temporary cart item:', err);
              });
            }
            
            return formattedPrice;
          }
          
          // Remove the item from cart if we added it temporarily and no discount
          if (shouldRemoveItem && itemToCheck.key) {
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                id: itemToCheck.key,
                quantity: 0
              })
            }).catch(err => {
              console.warn('Could not remove temporary cart item:', err);
            });
          }
        }
        
        return null;
      } catch (error) {
        console.error('Error fetching discounted price:', error);
        return null;
      }
    }

    /**
     * Format price from cents to match existing format
     */
    formatPriceFromCents(cents) {
      const amount = cents / 100;
      const oneTimePriceText = this.oneTimePrice || '$0.00';
      
      // Extract currency symbol and format from one-time price
      const currencyMatch = oneTimePriceText.match(/^[$€£¥]/);
      const currencySymbol = currencyMatch ? currencyMatch[0] : '$';
      
      // Check if original has currency code
      const hasCurrencyCode = /\b(USD|CAD|EUR|GBP|AUD|JPY|CNY|INR|MXN|BRL|ZAR|KRW|SGD|HKD|NZD|CHF|SEK|NOK|DKK|PLN|RUB|TRY|CZK|HUF|ILS|CLP|PHP|THB|MYR|IDR|VND|ARS)\b/i.test(oneTimePriceText);
      const currencyCode = oneTimePriceText.match(/\b(USD|CAD|EUR|GBP|AUD|JPY|CNY|INR|MXN|BRL|ZAR|KRW|SGD|HKD|NZD|CHF|SEK|NOK|DKK|PLN|RUB|TRY|CZK|HUF|ILS|CLP|PHP|THB|MYR|IDR|VND|ARS)\b/i)?.[0] || 'USD';
      
      const formatted = amount.toFixed(2);
      
      if (hasCurrencyCode) {
        return `${currencySymbol}${formatted} ${currencyCode}`;
      }
      
      return `${currencySymbol}${formatted}`;
    }

    /**
     * Update the price display with smooth transition
     * @param {string} newPrice - The new price to display
     * @param {boolean} isSubscription - Whether this is a subscription price
     * @param {string} originalPrice - Optional original price to show crossed out (for discounts)
     */
    updatePrice(newPrice, isSubscription, originalPrice = null) {
      if (!this.priceElement || !newPrice) {
        
        return;
      }

      if (!this.oneTimePrice) {
        this.oneTimePrice = this.extractPriceFromElement(this.priceElement);
      }

      // Find the price container (price__container or price element's parent)
      const priceContainer = this.priceElement.closest('.price__container') || 
                           this.priceElement.closest('.price') ||
                           this.priceElement.parentElement;
      
      if (!priceContainer) {
        
        return;
      }

      const currentPrice = this.extractPriceFromElement(this.priceElement);
      if (currentPrice === newPrice && !isSubscription && !originalPrice) {
        
        return;
      }

      // Double-check subscription state to prevent showing wrong price
      const isSubscriptionSelected = this.isSubscriptionCurrentlySelected();
      if (isSubscriptionSelected && !isSubscription) {
        // Subscription is selected but we're trying to show one-time price
        // Get subscription price instead
        if (this.widgetContainer) {
          const subPrice = this.getSubscriptionPriceFromWidget();
          if (subPrice) {
            this.subscriptionPrice = subPrice;
            this.fadeTransition(() => {
              this.showSubscriptionPrice(priceContainer, subPrice, this.oneTimePrice);
              
            });
            return;
          }
        }
      }

      this.fadeTransition(() => {
        if (isSubscription || originalPrice) {
          // Show subscription price or discounted one-time price with original price crossed out
          const originalPriceToShow = originalPrice || this.oneTimePrice;
          this.showSubscriptionPrice(priceContainer, newPrice, originalPriceToShow);
          
          // Only show discount badge for one-time discounts (not subscriptions)
          if (!isSubscription && originalPrice && this.discountPercentage) {
            this.showDiscountBadge(this.discountPercentage);
          } else {
            this.hideDiscountBadge();
          }
        } else {
          // Show regular one-time price
          this.showRegularPrice(priceContainer, newPrice);
          this.hideDiscountBadge();
        }
        
      });
    }

    /**
     * Show subscription price with original price crossed out
     */
    showSubscriptionPrice(priceContainer, subscriptionPrice, originalPrice) {
      // Find or create price__sale div
      let saleDiv = priceContainer.querySelector('.price__sale');
      if (!saleDiv) {
        saleDiv = document.createElement('div');
        saleDiv.className = 'price__sale';
        priceContainer.appendChild(saleDiv);
      }

      // Hide regular price div immediately to prevent flicker
      const regularDiv = priceContainer.querySelector('.price__regular');
      if (regularDiv) {
        regularDiv.style.display = 'none';
      }

      // Show sale price div
      saleDiv.style.display = 'block';

      // Check if price has large class (from price_class setting)
      const priceWrapper = priceContainer.closest('.price');
      const hasLargeClass = priceWrapper && priceWrapper.classList.contains('price--large');
      const salePriceClass = hasLargeClass 
        ? 'price-item price-item--sale price-item--last price--large'
        : 'price-item price-item--sale price-item--last';

      // Update sale price content - matching exact structure from price.liquid
      // Note: visually-hidden spans are for accessibility
      saleDiv.innerHTML = `
        <span class="visually-hidden visually-hidden--inline">Regular price</span>
        <span class="${salePriceClass}">
          ${subscriptionPrice}
        </span>
        <span>
          <s class="price-item price-item--regular">
            ${originalPrice}
          </s>
        </span>
        <span class="visually-hidden visually-hidden--inline">Sale price</span>
      `;

      // Add sale class to price container
      if (priceWrapper) {
        priceWrapper.classList.add('price--on-sale');
        priceWrapper.classList.add('price--subscription');
        // Add data attribute to prevent product-info.js from overwriting
        priceWrapper.setAttribute('data-recharge-price-locked', 'true');
      }
      
      // Hide discount badge for subscription (only show for one-time discounts)
      this.hideDiscountBadge();
    }

    /**
     * Show discount badge with percentage saved
     * @param {number} discountPercent - The discount percentage
     */
    showDiscountBadge(discountPercent) {
      if (!discountPercent || discountPercent <= 0) {
        this.hideDiscountBadge();
        return;
      }
      
      // Check if subscription is selected - don't show badge for subscriptions
      if (this.isSubscriptionCurrentlySelected()) {
        this.hideDiscountBadge();
        return;
      }
      
      // Function to update badge
      const updateBadge = () => {
        // First, try to update the badge in quantity variant picker if it exists
        const quantityVariantBadge = document.querySelector('[data-one-time-discount-badge]');
        if (quantityVariantBadge) {
          const discountPercentElement = quantityVariantBadge.querySelector('[data-discount-percent]');
          if (discountPercentElement) {
            discountPercentElement.textContent = `${discountPercent}% SAVED`;
          }
          // Use 'flex' to match the CSS class - override inline style
          quantityVariantBadge.style.display = 'flex';
        }
      };
      
      // Try to update immediately
      updateBadge();
      
      // If badge not found, try again after a short delay (in case DOM isn't ready)
      if (!document.querySelector('[data-one-time-discount-badge]')) {
        setTimeout(updateBadge, 100);
      }
      
      // Also update/create badge in quantity form (for regular quantity selector)
      const quantityForm = document.querySelector('[id^="Quantity-Form"]');
      if (quantityForm) {
        // Find quantity input element to position badge next to it
        const quantityInput = quantityForm.querySelector('quantity-input, .quantity');
        
        // Find or create badge container - position it after the quantity input
        let badgeContainer = quantityForm.querySelector('.one-time-discount-badge-container');
        if (!badgeContainer) {
          badgeContainer = document.createElement('div');
          badgeContainer.className = 'one-time-discount-badge-container';
          badgeContainer.style.cssText = 'display: inline-flex; align-items: center; margin-left: 8px; vertical-align: middle;';
          
          // Insert after quantity input if found, otherwise append to form
          if (quantityInput && quantityInput.parentElement) {
            quantityInput.parentElement.insertBefore(badgeContainer, quantityInput.nextSibling);
          } else {
            quantityForm.appendChild(badgeContainer);
          }
        }
        
        // Find or create badge
        let badge = badgeContainer.querySelector('.one-time-discount-badge');
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'one-time-discount-badge';
          badge.style.cssText = `
            display: inline-flex;
            align-items: center;
            gap: 4px;
            padding: 8px 12px;
            border-radius: 30px;
            font-weight: 700;
            font-size: 14px;
            text-transform: uppercase;
            background: #B8EDC6;
            color: #151F6D;
            line-height: 14px;
            letter-spacing: 1px;
            white-space: nowrap;
          `;
          badgeContainer.appendChild(badge);
        }
        
        // Add checkmark icon
        const checkmarkSvg = `
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" style="flex-shrink: 0;">
            <path d="M11.6667 3.5L5.25 9.91667L2.33334 7" stroke="#151F6D" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `;
        
        badge.innerHTML = `${checkmarkSvg} ${discountPercent}% SAVED`;
        badgeContainer.style.display = 'inline-flex';
      }
    }

    /**
     * Hide discount badge
     */
    hideDiscountBadge() {
      // Hide badge in quantity variant picker
      const quantityVariantBadge = document.querySelector('[data-one-time-discount-badge]');
      if (quantityVariantBadge) {
        quantityVariantBadge.style.display = 'none';
      }
      
      // Hide badge in quantity form
      const quantityForm = document.querySelector('[id^="Quantity-Form"]');
      if (quantityForm) {
        const badgeContainer = quantityForm.querySelector('.one-time-discount-badge-container');
        if (badgeContainer) {
          badgeContainer.style.display = 'none';
        }
      }
    }

    /**
     * Show regular one-time price
     */
    showRegularPrice(priceContainer, price) {
      // Hide sale price div
      const saleDiv = priceContainer.querySelector('.price__sale');
      if (saleDiv) {
        saleDiv.style.display = 'none';
      }

      // Show regular price div
      const regularDiv = priceContainer.querySelector('.price__regular');
      if (regularDiv) {
        regularDiv.style.display = '';
        // Update the price in regular div
        const priceItem = regularDiv.querySelector('.price-item--regular');
        if (priceItem) {
          priceItem.textContent = price;
        } else {
          // If no price item found, try to find any price-item in regular div
          const anyPriceItem = regularDiv.querySelector('.price-item');
          if (anyPriceItem) {
            anyPriceItem.textContent = price;
          } else {
            // If still no price item, update the regular div's text content
            regularDiv.textContent = price;
          }
        }
      } else {
        // If no regular div, update the element directly
        if (this.priceElement) {
          this.priceElement.textContent = price;
        }
      }

      // Remove sale class from price container
      const priceWrapper = priceContainer.closest('.price') || priceContainer;
      if (priceWrapper) {
        priceWrapper.classList.remove('price--on-sale');
        priceWrapper.classList.remove('price--subscription');
        // Remove lock attribute
        priceWrapper.removeAttribute('data-recharge-price-locked');
      }
      
      // Hide discount badge for regular price
      this.hideDiscountBadge();
    }

    /**
     * Apply fade transition effect
     */
    fadeTransition(callback) {
      const element = this.priceElement;
      if (!element) return;

      const originalTransition = element.style.transition;
      const originalOpacity = element.style.opacity;

      element.style.transition = `opacity ${CONFIG.TRANSITION_DURATION / 2}ms ease-in-out`;
      element.style.opacity = CONFIG.FADE_OPACITY.toString();

      setTimeout(() => {
        if (callback) callback();

        element.style.opacity = '1';

        setTimeout(() => {
          element.style.transition = originalTransition || '';
          element.style.opacity = originalOpacity || '';
        }, CONFIG.TRANSITION_DURATION / 2);
      }, CONFIG.TRANSITION_DURATION / 2);
    }

    /**
     * Watch for widget updates
     */
    watchWidgetUpdates() {
      if (!this.widgetContainer) return;

      const updateObserver = new MutationObserver((mutations) => {
        const currentWidget = document.querySelector(CONFIG.WIDGET_CONTAINER);
        if (!currentWidget) {
          this.isInitialized = false;
          this.widgetContainer = null;
          this.watchForWidget();
          return;
        }

        const newSubscriptionPrice = this.getSubscriptionPriceFromWidget();
        if (newSubscriptionPrice && newSubscriptionPrice !== this.subscriptionPrice) {
          this.subscriptionPrice = newSubscriptionPrice;
          
          if (this.isSubscription) {
            this.updatePrice(this.subscriptionPrice, true);
          }
        }

        const radioInputs = this.widgetContainer.querySelectorAll(CONFIG.RADIO_INPUT);
        if (radioInputs.length > 0) {
          radioInputs.forEach(radio => {
            if (!radio.hasAttribute('data-recharge-listener-attached')) {
              radio.addEventListener('change', this.handleRadioChange);
              radio.setAttribute('data-recharge-listener-attached', 'true');
            }
          });
        }
      });

      updateObserver.observe(this.widgetContainer, {
        childList: true,
        subtree: true,
        attributes: false
      });
    }

    /**
     * Cleanup and disconnect observers
     */
    destroy() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      if (this.radioObserver) {
        this.radioObserver.disconnect();
        this.radioObserver = null;
      }

      if (this.variantObserver) {
        this.variantObserver.disconnect();
        this.variantObserver = null;
      }

      if (this.widgetContainer) {
        const radioInputs = this.widgetContainer.querySelectorAll(CONFIG.RADIO_INPUT);
        radioInputs.forEach(radio => {
          radio.removeEventListener('change', this.handleRadioChange);
        });
      }

      this.isInitialized = false;
    }
  }

  // Initialize on page load
  const priceUpdater = new RechargePriceUpdater();
  priceUpdater.init();

  // Re-initialize on dynamic page updates
  if (typeof window !== 'undefined') {
    document.addEventListener('shopify:section:load', () => {
      setTimeout(() => {
        const newUpdater = new RechargePriceUpdater();
        newUpdater.init();
      }, 100);
    });

    if (window.location.hostname === 'localhost' || window.location.hostname.includes('myshopify.com')) {
      window.RechargePriceUpdater = priceUpdater;
    }
  }
})();
